<template>
  <div class="report-bottom">
    <section class="card" aria-labelledby="basisTitle">
      <h2 class="card-title" id="basisTitle"><span class="ttl">Inspection basis</span></h2>
      <div class="card-body">
        <dl class="basis-list">
          <template v-for="row in basis">
            <dt :key="`${row.label}-dt`">{{ row.label }}</dt>
            <dd :key="`${row.label}-dd`">{{ row.value }}</dd>
          </template>
        </dl>
      </div>
    </section>

    <section class="card" aria-labelledby="legendTitle">
      <h2 class="card-title" id="legendTitle"><span class="ttl">Legend</span></h2>
      <div class="card-body legend-grid">
        <div>
          <div class="legend-title">Contour confidence<br /><span class="legend-title__sub">(finding outline)</span></div>
          <div v-for="row in contour" :key="row.bucket" class="legend-row"><span class="swatch" :class="`swatch--${row.bucket}`"></span><span>{{ row.label }}</span></div>
        </div>
        <div>
          <div class="legend-title">Scab measure<br />recommendation</div>
          <div v-for="row in scab" :key="row.value" class="legend-row"><span class="chip" :class="`chip--scab-${row.value.toLowerCase()}`">{{ row.value }}</span><span>{{ row.label }}</span></div>
        </div>
      </div>
    </section>

    <section class="card validation-card" :class="{ 'is-approved': approved }" aria-labelledby="validationTitle">
      <h2 class="card-title" id="validationTitle"><span class="ttl">Validation status</span></h2>
      <div class="card-body validation-body">
        <v-icon :size="30" class="validation-icon">{{ validation.icon }}</v-icon>
        <div>
          <p class="validation-text">{{ validation.lead }}<strong v-if="validation.emphasis">{{ validation.emphasis }}</strong>{{ validation.trail }}</p>
          <p class="validation-foot">{{ validation.foot }}</p>
        </div>
      </div>
    </section>

    <section class="card" aria-labelledby="notesTitle">
      <h2 class="card-title" id="notesTitle"><span class="ttl">Notes</span></h2>
      <div class="card-body notes-body">
        <ul>
          <li v-for="note in notes" :key="note">{{ note }}</li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script>
  import { REVIEW_STATUS } from '../../utils/findingsMetrics';
  import { INSPECTION_BASIS, CONTOUR_LEGEND, SCAB_LEGEND, VALIDATION_PENDING, VALIDATION_APPROVED, REPORT_NOTES } from './reportConstants';

  export default {
    name: 'ReportFooter',
    props: {
      reviewStatus: { type: String, default: REVIEW_STATUS.PENDING },
    },
    data() {
      return { basis: INSPECTION_BASIS, contour: CONTOUR_LEGEND, scab: SCAB_LEGEND, notes: REPORT_NOTES };
    },
    computed: {
      approved() {
        return this.reviewStatus === REVIEW_STATUS.APPROVED;
      },
      validation() {
        return this.approved ? VALIDATION_APPROVED : VALIDATION_PENDING;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .report-bottom {
    display: grid;
    grid-template-columns: minmax(0, 26fr) minmax(0, 22fr) minmax(0, 24fr) minmax(0, 28fr);
    gap: var(--ds-space-sm);
    align-items: stretch;
  }
  .card-body { padding: 6px var(--ds-space-md) var(--ds-space-md); font-size: var(--ds-font-body-sm); color: var(--aa-text-primary); line-height: 1.5; }

  .basis-list { margin: 0; display: grid; grid-template-columns: max-content 1fr; gap: 6px var(--ds-space-md); }
  .basis-list dt { font-weight: 600; white-space: nowrap; }
  .basis-list dd { margin: 0; color: var(--aa-text-secondary); }

  .legend-grid { display: grid; grid-template-columns: 1fr 1fr; gap: var(--ds-space-md); }
  .legend-title { font-weight: 600; margin-bottom: 6px; }
  .legend-title__sub { font-weight: 400; color: var(--aa-text-muted); }
  .legend-row { display: flex; align-items: flex-start; gap: 8px; margin-bottom: 6px; font-size: var(--ds-font-caption); color: var(--aa-text-secondary); }
  .swatch { flex: 0 0 auto; width: 22px; height: 14px; border-radius: 3px; border: 2px solid; background: transparent; margin-top: 1px; }
  .swatch--high { border-color: var(--aa-contour-high); }
  .swatch--moderate { border-color: var(--aa-contour-moderate); }
  .swatch--low { border-color: var(--aa-contour-low); }

  .chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    height: 20px;
    min-width: 74px;
    padding: 0 8px;
    border-radius: 2px;
    font-size: var(--aa-font-micro);
    font-weight: 600;
    letter-spacing: 0.04em;
    border: 1px solid transparent;
    white-space: nowrap;
  }
  .chip--scab-no { background: rgba(3, 98, 76, 0.1); color: var(--aa-scab-no); border-color: rgba(3, 98, 76, 0.3); }
  .chip--scab-possible { background: rgba(180, 83, 9, 0.12); color: var(--aa-scab-possible); border-color: rgba(180, 83, 9, 0.32); }
  .chip--scab-yes { background: rgba(183, 28, 28, 0.1); color: var(--aa-scab-yes); border-color: rgba(183, 28, 28, 0.3); }

  .validation-body { display: flex; gap: var(--ds-space-md); align-items: flex-start; }
  .validation-icon { color: var(--aa-review-pending) !important; }
  .validation-card.is-approved .validation-icon { color: var(--aa-review-approved) !important; }
  .validation-text { margin: 0; }
  .validation-text strong { font-weight: 700; }
  .validation-foot { margin: var(--ds-space-sm) 0 0; font-weight: 600; }

  .notes-body ul { margin: 0; padding-left: 18px; color: var(--aa-text-secondary); }
  .notes-body li { margin-bottom: 4px; }

  @media (max-width: 1500px) {
    .report-bottom { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (max-width: 760px) {
    .report-bottom { grid-template-columns: minmax(0, 1fr); }
    .legend-grid { grid-template-columns: 1fr; }
  }
</style>
